import { SupabaseClient } from '@supabase/supabase-js';
import { CollectionShare, CollectionShareWithDetails } from '../../../src/types/Collections';

/**
 * Share a collection with other vault members
 */
export async function shareCollectionCore(
    supabase: SupabaseClient,
    collectionId: string,
    sharedToEmails: string[]
): Promise<{ success: boolean; error?: string }> {
    try {
        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            return { success: false, error: 'User not authenticated' };
        }

        // Verify user owns the collection (contact_collections uses user_id as owner)
        const { data: collection, error: collectionError } = await supabase
            .from('contact_collections')
            .select('id, user_id')
            .eq('id', collectionId)
            .eq('user_id', user.id)
            .single();

        if (collectionError || !collection) {
            return { success: false, error: 'Collection not found or access denied' };
        }

        // Create share records
        const shares = sharedToEmails.map(email => ({
            collection_id: collectionId,
            shared_by_user_id: user.id,
            shared_to_email: (email || '').toLowerCase(),
            status: 'pending' as const
        }));

        const { error: insertError } = await supabase
            .from('collection_shares')
            .insert(shares);

        if (insertError) {
            return { success: false, error: insertError.message };
        }

        return { success: true };
    } catch (error) {
        console.error('Error sharing collection:', error);
        return { success: false, error: 'Failed to share collection' };
    }
}

/**
 * Get collection shares for a specific collection
 */
export async function getCollectionSharesCore(
    supabase: SupabaseClient,
    collectionId: string
): Promise<{ success: boolean; data?: CollectionShare[]; error?: string }> {
    try {
        const { data, error } = await supabase
            .from('collection_shares')
            .select('*')
            .eq('collection_id', collectionId)
            .order('created_at', { ascending: false });

        if (error) {
            return { success: false, error: error.message };
        }

        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching collection shares:', error);
        return { success: false, error: 'Failed to fetch collection shares' };
    }
}

/**
 * Get all shares received by the current user
 */
export async function getReceivedSharesCore(
    supabase: SupabaseClient
): Promise<{ success: boolean; data?: CollectionShareWithDetails[]; error?: string }> {
    try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            return { success: false, error: 'User not authenticated' };
        }

                const userEmail = (user.email || '').toLowerCase();
                const { data, error } = await supabase
                        .from('collection_shares')
                        .select(`
                *,
                collection:contact_collections(*),
                shared_by_user:user_profiles!shared_by_user_id(
                    id,
                    email,
                    first_name,
                    last_name,
                    avatar_url
                )
            `)
                        .or(`shared_to_email.eq.${userEmail},shared_to_user_id.eq.${user.id}`)
                        .order('created_at', { ascending: false });

        if (error) {
            return { success: false, error: error.message };
        }

        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching received shares:', error);
        return { success: false, error: 'Failed to fetch received shares' };
    }
}

/**
 * Accept or decline a collection share
 */
export async function updateShareStatusCore(
    supabase: SupabaseClient,
    shareId: string,
    status: 'accepted' | 'declined'
): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            return { success: false, error: 'User not authenticated' };
        }

        const updateData: any = { status };
        if (status === 'accepted') {
            updateData.accepted_at = new Date().toISOString();
            updateData.shared_to_user_id = user.id;
        } else if (status === 'declined') {
            updateData.declined_at = new Date().toISOString();
        }

        const userEmailForMatch = (user.email || '').toLowerCase();
        const { error } = await supabase
            .from('collection_shares')
            .update(updateData)
            .eq('id', shareId)
            .or(`shared_to_email.eq.${userEmailForMatch},shared_to_user_id.eq.${user.id}`);

        if (error) {
            return { success: false, error: error.message };
        }

        return { success: true };
    } catch (error) {
        console.error('Error updating share status:', error);
        return { success: false, error: 'Failed to update share status' };
    }
}

/**
 * Remove a collection share
 */
export async function removeCollectionShareCore(
    supabase: SupabaseClient,
    shareId: string
): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            return { success: false, error: 'User not authenticated' };
        }

        const { error } = await supabase
            .from('collection_shares')
            .delete()
            .eq('id', shareId)
            .eq('shared_by_user_id', user.id);

        if (error) {
            return { success: false, error: error.message };
        }

        return { success: true };
    } catch (error) {
        console.error('Error removing collection share:', error);
        return { success: false, error: 'Failed to remove collection share' };
    }
}

/**
 * Get vault members (users in the same vault/group for collection sharing)
 */
export async function getVaultMembersCore(
    supabase: SupabaseClient
): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            return { success: false, error: 'User not authenticated' };
        }

        // First, find which vault (group) the user belongs to
        const { data: userMemberships, error: memberError } = await supabase
            .from('vault_members')
            .select('group_id')
            .eq('user_id', user.id);

        if (memberError) {
            return { success: false, error: 'Failed to check group membership' };
        }

        if (!userMemberships || userMemberships.length === 0) {
            return { success: true, data: [] }; // User not in any group
        }

        // For now, use the first group (most users will only be in one)
        const groupId = userMemberships[0].group_id;

        // Get all members of this group (excluding current user)
        const { data: groupMembers, error: groupError } = await supabase
            .from('vault_members')
            .select('user_id')
            .eq('group_id', groupId)
            .neq('user_id', user.id);

        if (groupError) {
            return { success: false, error: 'Failed to fetch group members' };
        }

        if (!groupMembers || groupMembers.length === 0) {
            return { success: true, data: [] };
        }

        const memberUserIds = groupMembers.map(m => m.user_id);

        // Get user profiles for these members
        const { data: userProfiles, error: profilesError } = await supabase
            .from('user_profiles')
            .select('user_id, first_name, last_name, email, avatar_url, photo_url, claimed_contact_id')
            .in('user_id', memberUserIds);

        if (profilesError) {
            return { success: false, error: 'Failed to fetch member profiles' };
        }

        // Get member-chosen contacts for this vault
        const { data: vaultContacts, error: vaultContactsError } = await supabase
            .from('vault_member_contacts')
            .select('user_id, contact_id')
            .eq('group_id', groupId)
            .in('user_id', memberUserIds);

        // Not fatal if this fails
        const chosenContacts: Record<string, string> = {};
        if (!vaultContactsError && vaultContacts) {
            vaultContacts.forEach(vc => {
                chosenContacts[vc.user_id] = vc.contact_id;
            });
        }

        // Get contact details for claimed and chosen contacts
        const contactIds = Array.from(new Set([
            ...(userProfiles || []).map(p => p.claimed_contact_id).filter(Boolean),
            ...Object.values(chosenContacts)
        ]));

        let contactDetails: Record<string, any> = {};
        if (contactIds.length > 0) {
            const { data: contacts, error: contactsError } = await supabase
                .from('contacts')
                .select('id, first_name, last_name, profile_image_url, photo')
                .in('id', contactIds);

            if (!contactsError && contacts) {
                contacts.forEach(c => {
                    contactDetails[c.id] = {
                        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Contact',
                        avatar: c.profile_image_url || c.photo
                    };
                });
            }
        }

        // Format the results to match what ShareCollectionModal expects
        const formattedMembers = (userProfiles || []).map(profile => {
            const chosenContactId = chosenContacts[profile.user_id];
            const claimedContactId = profile.claimed_contact_id;

            // Prefer chosen contact over claimed contact
            const contactId = chosenContactId || claimedContactId;
            const contact = contactId ? contactDetails[contactId] : null;

            const displayName = contact?.name ||
                [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
                (profile.email ? profile.email.split('@')[0] : '') ||
                'Member';

            const avatar = contact?.avatar || profile.avatar_url || profile.photo_url;

            return {
                id: profile.user_id,
                email: profile.email,
                first_name: profile.first_name,
                last_name: profile.last_name,
                profile_image_url: avatar,
                plan_type: 'family' // Since they're in a vault, assume they have access
            };
        });

        return { success: true, data: formattedMembers };
    } catch (error) {
        console.error('Error fetching vault members:', error);
        return { success: false, error: 'Failed to fetch vault members' };
    }
}

/**
 * Check if user has access to a collection (owns it or has accepted share)
 */
export async function hasCollectionAccessCore(
    supabase: SupabaseClient,
    collectionId: string
): Promise<{ success: boolean; hasAccess?: boolean; error?: string }> {
    try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            return { success: false, error: 'User not authenticated' };
        }

        // Check if user owns the collection
        const { data: ownedCollection } = await supabase
            .from('contact_collections')
            .select('id')
            .eq('id', collectionId)
            .eq('user_id', user.id)
            .single();

        if (ownedCollection) {
            return { success: true, hasAccess: true };
        }

        // Check if user has accepted share for this collection
        const { data: acceptedShare } = await supabase
            .from('collection_shares')
            .select('id')
            .eq('collection_id', collectionId)
            .eq('status', 'accepted')
            .or(`shared_to_email.eq.${user.email},shared_to_user_id.eq.${user.id}`)
            .single();

        return { success: true, hasAccess: !!acceptedShare };
    } catch (error) {
        console.error('Error checking collection access:', error);
        return { success: false, error: 'Failed to check collection access' };
    }
}